import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ShellService } from '../../shell/shell.service';
import { PhotoChange, PhotoEditorComponent } from '../photo-editor.component';

/**
 * Settings > My Account > Profile, Password & Preferences (mockup settings #accountPanel), for the
 * signed-in user:
 *   - Profile Picture: the user's photo on the tenant's Spaces bucket (api/account/photo), shown in
 *     the top bar and in Users > Edit;
 *   - Change Password: api/account/password checks the current one; at least 8 characters;
 *   - Display & Accessibility: Dark Mode / Color Blind Mode, the same per-user preferences as the
 *     sidebar switches.
 * Served at /my-account and the old /change-password.
 */
@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent implements OnInit {
  @ViewChild(PhotoEditorComponent) photoEditor: PhotoEditorComponent;

  // Profile Picture
  name = '';
  photoUrl: string = null;
  photoChange: PhotoChange = null;
  photoMessage = '';
  photoError = '';
  savingPhoto = false;

  // Change Password
  current = '';
  next = '';
  confirm = '';
  errors: { [field: string]: string } = {};
  message = '';
  error = '';
  saving = false;

  // Display & Accessibility
  darkMode = false;
  colorblindMode = false;
  prefsMessage = '';

  constructor(private http: HttpClient, private shell: ShellService) { }

  ngOnInit() {
    this.http.get<{ name: string, photo_url: string }>(environment.api + 'account').subscribe(
      res => {
        this.name = res.name;
        this.photoUrl = res.photo_url;
      },
      () => this.photoError = 'Your profile could not be loaded.'
    );
    this.resetPreferences();
  }

  // ---- Profile Picture ----

  onPhotoChanged(change: PhotoChange) {
    this.photoChange = change;
    this.photoMessage = '';
    this.photoError = '';
  }

  cancelPhoto() {
    this.photoChange = null;
    this.photoError = '';
    if (this.photoEditor) {
      this.photoEditor.reset();
    }
  }

  savePhoto() {
    const change = this.photoChange;
    if (!change) {
      return;
    }
    const data = new FormData();
    if (change.file) {
      data.append('photo', change.file, change.file.name);
    } else {
      data.append('remove_photo', '1');
    }
    this.savingPhoto = true;
    this.photoError = '';
    this.http.post<{ message: string, photo_url: string }>(environment.api + 'account/photo', data).subscribe(
      res => {
        this.savingPhoto = false;
        this.photoChange = null;
        this.photoUrl = res.photo_url;   // the editor resets to it
        this.photoMessage = res.message || 'Profile picture saved.';
        this.shell.setAccountPhoto(res.photo_url);
      },
      err => {
        this.savingPhoto = false;
        this.photoError = typeof err === 'string' ? err : 'The picture could not be saved.';
      }
    );
  }

  // ---- Change Password ----

  save() {
    const e: { [field: string]: string } = {};
    if (!this.current) { e.current = 'Enter your current password.'; }
    if (this.next.length < 8) { e.next = 'Use at least 8 characters.'; }
    if (this.confirm !== this.next) { e.confirm = 'The passwords do not match.'; }
    this.errors = e;
    this.message = '';
    this.error = '';
    if (Object.keys(e).length) {
      return;
    }
    this.saving = true;
    this.http.post<any>(environment.api + 'account/password', {
      current_password: this.current, new_password: this.next, new_password_confirmation: this.confirm,
    }).subscribe(
      res => {
        this.saving = false;
        this.message = res.message || 'Password updated.';
        this.current = this.next = this.confirm = '';
      },
      err => {
        this.saving = false;
        this.error = typeof err === 'string' ? err : 'The password could not be changed.';
      }
    );
  }

  // ---- Display & Accessibility ----

  get preferencesChanged(): boolean {
    return this.darkMode !== this.shell.isModeOn('dark-mode') || this.colorblindMode !== this.shell.isModeOn('colorblind-mode');
  }

  resetPreferences() {
    this.darkMode = this.shell.isModeOn('dark-mode');
    this.colorblindMode = this.shell.isModeOn('colorblind-mode');
  }

  savePreferences() {
    this.shell.setMode('dark-mode', this.darkMode);
    this.shell.setMode('colorblind-mode', this.colorblindMode);
    this.prefsMessage = 'Preferences saved.';
  }
}
